export function isObject(item: unknown): item is Record<string, unknown> {
  return item !== null && typeof item === 'object' && !Array.isArray(item)
}

export function mergeDeep<T extends Record<string, unknown>>(
  source: T,
  target?: unknown,
): T {
  if (!target || !isObject(target)) {
    return source
  }

  const output = { ...source }

  if (isObject(target)) {
    Object.keys(target).forEach((key) => {
      const sourceValue = source[key]
      const targetValue = (target as Record<string, unknown>)[key]

      if (targetValue === null || targetValue === undefined) {
        return
      }

      if (isObject(targetValue)) {
        if (!(key in source)) {
          ;(output as Record<string, unknown>)[key] = targetValue
        } else {
          ;(output as Record<string, unknown>)[key] = mergeDeep(
            sourceValue as Record<string, unknown>,
            targetValue as Record<string, unknown>,
          )
        }
      } else if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
        const targetArray = targetValue as unknown[]
        const sourceArray = sourceValue as unknown[]
        ;(output as Record<string, unknown>)[key] = [
          ...new Set([...targetArray, ...sourceArray]),
        ]
      } else {
        ;(output as Record<string, unknown>)[key] = targetValue
      }
    })
  }

  return output
}

export function omitEmpty<T extends Record<string, any>>(obj: T) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== null && value !== undefined && value !== '',
    ),
  ) as T
}

export function omitIfEqual<T extends Record<string, any>>(
  initialValues: T | undefined,
  newValues: Partial<T>,
  fields: (keyof T)[],
) {
  if (!initialValues) return newValues as T
  const result: Partial<T> = { ...newValues }
  for (const key of fields) {
    if (newValues[key] === initialValues[key]) {
      delete result[key]
    }
  }
  return result as T
}
